import React from 'react';
import { Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, IconButton, TextField } from '@biq/apex-ui';
import { getEmptyErrorMessage } from '../../edge-definition-helper';
import {
    AttributeAssociationSelectionProps,
    attributeAssociationSelectionStyles,
} from './create-edge-definition-helper';

export const AttributeAssociationSelection = ({
    leftSideSelectionOptions,
    rightSideSelectionOptions,
    handleRemove,
    attributeAssociationItem,
    onAttributeAssociationChanged,
    isFirstItem,
}: AttributeAssociationSelectionProps): React.JSX.Element => {
    const handleLeftSideChange = (newValue: string): void => {
        const isLeftSideValid: boolean = !!newValue;
        const isRightSideValid: boolean =
            !!attributeAssociationItem.attributeAssociation.rightSideValue;
        const displayBothEmpty: boolean = !isLeftSideValid && !isRightSideValid;

        onAttributeAssociationChanged({
            ...attributeAssociationItem,
            attributeAssociation: {
                ...attributeAssociationItem.attributeAssociation,
                leftSideValue: newValue,
            },
            leftSideError: !newValue,
            leftSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !isLeftSideValid
            ),
            rightSideError: !isRightSideValid,
            rightSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !isRightSideValid
            ),
        });
    };

    const handleRightSideChange = (newValue: string): void => {
        const isRightSideValid: boolean = !!newValue;
        const isLeftSideValid: boolean =
            !!attributeAssociationItem.attributeAssociation.leftSideValue;
        const displayBothEmpty: boolean = !isLeftSideValid && !isRightSideValid;

        onAttributeAssociationChanged({
            ...attributeAssociationItem,
            attributeAssociation: {
                ...attributeAssociationItem.attributeAssociation,
                rightSideValue: newValue,
            },
            leftSideError: !isLeftSideValid,
            leftSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !isLeftSideValid
            ),
            rightSideError: !newValue,
            rightSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !isRightSideValid
            ),
        });
    };

    return (
        <Grid
            sx={
                attributeAssociationSelectionStyles.attributeAssociationSelectionContainer
            }
        >
            <Grid
                sx={
                    attributeAssociationSelectionStyles.associationLeftSideContainer
                }
            >
                <Autocomplete
                    options={leftSideSelectionOptions}
                    onChange={(_event, newValue): void =>
                        handleLeftSideChange(newValue)
                    }
                    sx={
                        isFirstItem
                            ? attributeAssociationSelectionStyles.associationAutocomplete
                            : attributeAssociationSelectionStyles.nonFirstLeftAssociationAutocomplete
                    }
                    renderInput={(params): React.JSX.Element => (
                        <TextField
                            {...params}
                            size='medium'
                            required={true}
                            label='From Attribute'
                            variant='outlined'
                            errorText={
                                attributeAssociationItem.leftSideErrorMessage
                            }
                            error={attributeAssociationItem.leftSideError}
                        />
                    )}
                />
            </Grid>
            <Grid
                sx={
                    isFirstItem
                        ? attributeAssociationSelectionStyles.associationRightSideContainer
                        : attributeAssociationSelectionStyles.nonFirstItemAssociationRightSideContainer
                }
            >
                <Autocomplete
                    options={rightSideSelectionOptions}
                    onChange={(_event, newValue): void =>
                        handleRightSideChange(newValue)
                    }
                    sx={
                        isFirstItem
                            ? attributeAssociationSelectionStyles.associationAutocomplete
                            : attributeAssociationSelectionStyles.nonFirstItemRightSideAssociationAutocomplete
                    }
                    renderInput={(params): React.JSX.Element => (
                        <TextField
                            {...params}
                            size='medium'
                            required={true}
                            label='To Attribute'
                            variant='outlined'
                            errorText={
                                attributeAssociationItem.rightSideErrorMessage
                            }
                            error={attributeAssociationItem.rightSideError}
                        />
                    )}
                />
            </Grid>
            {!isFirstItem && (
                <Grid
                    sx={
                        attributeAssociationSelectionStyles.deleteAssociationButtonContainer
                    }
                >
                    <IconButton
                        color='grayalt'
                        size='small'
                        buttonType='closeButton'
                        onClick={handleRemove}
                        sx={attributeAssociationSelectionStyles.deleteButton}
                    >
                        <CloseIcon />
                    </IconButton>
                </Grid>
            )}
        </Grid>
    );
};
